import { DIET_RULES, type DietRuleId } from '../domain/diet';
import { STAPLE_CHOICES } from '../domain/staples';
import { type IngredientId, labelName } from '../domain/taxonomy';
import { sanitizeInput } from '../domain/text';

function toggle<T>(list: T[], x: T): T[] {
  return list.includes(x) ? list.filter((y) => y !== x) : [...list, x];
}

export function RulesEditor({ rules, other, onChange }: {
  rules: DietRuleId[];
  other: string;
  onChange: (rules: DietRuleId[], other: string) => void;
}) {
  const groups: { kind: 'allergy' | 'diet' | 'religious'; title: string }[] = [
    { kind: 'allergy', title: 'Allergies' },
    { kind: 'diet', title: 'Diet' },
    { kind: 'religious', title: 'Faith & preference' },
  ];
  return (
    <div className="grid gap-5">
      {groups.map((g) => (
        <fieldset key={g.kind}>
          <legend className="eyebrow mb-2">{g.title}</legend>
          <div className="flex flex-wrap gap-2">
            {DIET_RULES.filter((r) => r.kind === g.kind).map((r) => (
              <button key={r.id} type="button" className="chip" aria-pressed={rules.includes(r.id)}
                onClick={() => onChange(toggle(rules, r.id), other)}>
                {r.label}
              </button>
            ))}
          </div>
        </fieldset>
      ))}
      <div>
        <label htmlFor="other-allergies" className="eyebrow block mb-2">Anything else? (we’ll remind you to check labels)</label>
        <input id="other-allergies" className="input" maxLength={200} value={other} placeholder="e.g. mustard, celery, kiwi"
          onChange={(e) => onChange(rules, sanitizeInput(e.target.value, 200))} />
      </div>
    </div>
  );
}

export function StaplesEditor({ staples, onChange }: { staples: IngredientId[]; onChange: (s: IngredientId[]) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STAPLE_CHOICES.map((id) => (
        <button key={id} type="button" className="chip" aria-pressed={staples.includes(id)}
          onClick={() => onChange(toggle(staples, id))}>{labelName(id)}
        </button>
      ))}
    </div>
  );
}

export function Stepper({ value, min, max, onChange, label, unit }: {
  value: number; min: number; max: number; onChange: (n: number) => void; label: string; unit?: string;
}) {
  return (
    <div className="flex items-center gap-3" role="group" aria-label={label}>
      <button type="button" className="icon-btn border border-line-strong" aria-label={`Fewer ${label.toLowerCase()}`}
        disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <span className="min-w-[4ch] text-center font-semibold tabular-nums" aria-live="polite">{value}{unit ? ` ${unit}` : ''}</span>
      <button type="button" className="icon-btn border border-line-strong" aria-label={`More ${label.toLowerCase()}`}
        disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  );
}
